import { Button, Flex, QRCode, Skeleton } from 'antd'
import { RefreshCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useStyles } from 'src/features/auth/styles'

import { QR_CODE_STATUS, QR_PANEL_STATUS } from './constants'
import type { QrPanelState, QrPanelVariant } from './types'
import { toQrCodeStatus } from './utils'

type QrCodeBoxProps = {
  variant: QrPanelVariant
  status: QrPanelState['status']
  qrPayload?: string
  shouldShowRefreshOverlay: boolean
  isRefreshPending: boolean
  onRefresh: () => void
  onRetry: () => void
}

export const QrCodeBox = ({
  variant,
  status,
  qrPayload,
  shouldShowRefreshOverlay,
  isRefreshPending,
  onRefresh,
  onRetry
}: QrCodeBoxProps) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const isStarting = status === QR_PANEL_STATUS.STARTING
  const qrSize = variant === 'compact' ? 120 : 220

  return (
    <Flex align="center" justify="center">
      <Flex
        align="center"
        justify="center"
        className={cx(
          styles.qrCodeFrame,
          variant === 'compact' && styles.qrCompactCodeFrame
        )}
      >
        {isStarting ? (
          <Skeleton.Node
            active
            className={
              variant === 'compact'
                ? styles.qrCompactSkeleton
                : styles.qrSkeleton
            }
          />
        ) : (
          <QRCode
            value={qrPayload ?? ' '}
            size={qrSize}
            bgColor="#ffffff"
            color="rgba(13, 22, 30, 0.88)"
            status={
              variant === 'compact'
                ? QR_CODE_STATUS.ACTIVE
                : toQrCodeStatus(status)
            }
            onRefresh={variant === 'compact' ? undefined : onRetry}
            errorLevel="M"
          />
        )}

        {shouldShowRefreshOverlay && (
          <Flex
            align="center"
            justify="center"
            className={styles.qrRefreshOverlay}
          >
            <Button
              shape="round"
              icon={<RefreshCcw size={16} />}
              onClick={onRefresh}
              loading={isRefreshPending || isStarting}
              disabled={isRefreshPending || isStarting}
              className={styles.qrRefreshButton}
            >
              {t('auth.qrSignIn.refresh')}
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
